import moment, { MomentInput } from 'moment';
import { Option } from '../components/select/select.component';
import { Trademark, TrademarkDTO, TrademarkSynchronizationFilter } from '../models/trademark';

// Translation key explaining a failed synchronisation; also reads the codes found in older bulk reports
export function synchronizationFailureKey(failure?: string): string {
  switch (failure?.toUpperCase()) {
    case 'NOT_FOUND':
    case 'TRADEMARK_NOT_FOUND':
      return 'SYNC.FAILURES.NOT_FOUND';
    case 'AMBIGUOUS':
      return 'SYNC.FAILURES.AMBIGUOUS';
    case 'UNAVAILABLE':
    case 'EXTERNAL_TRADEMARK_NOT_FOUND':
      return 'SYNC.FAILURES.UNAVAILABLE';
    case 'INCOMPLETE':
      return 'SYNC.FAILURES.INCOMPLETE';
    case 'APPLICATION_DATE_DIFFERS':
      return 'SYNC.FAILURES.APPLICATION_DATE_DIFFERS';
    case 'CLASSES_DIFFER':
      return 'SYNC.FAILURES.CLASSES_DIFFER';
    case 'NAME_DIFFERS':
      return 'SYNC.FAILURES.NAME_DIFFERS';
    case 'NUMBER_IN_OTHER_FIELD':
      return 'SYNC.FAILURES.NUMBER_IN_OTHER_FIELD';
    case 'NOT_CONFIRMED':
      return 'SYNC.FAILURES.NOT_CONFIRMED';
    default:
      return 'ERRORS.GENERIC';
  }
}

// A new array on each call: app-select mutates the options it receives
export function synchronizationFilterOptions(): Option[] {
  return [
    { label: 'SYNC.STATE_SUCCESS', value: TrademarkSynchronizationFilter.SUCCESS },
    { label: 'SYNC.STATE_FAILED', value: TrademarkSynchronizationFilter.FAILED },
    { label: 'SYNC.NEVER_SYNCHRONIZED', value: TrademarkSynchronizationFilter.NEVER },
  ];
}

// The fields a synchronisation from the trademark page writes; the identification number and the territory never change
export const SYNCHRONIZED_FIELDS = [
  'name',
  'registrationNumber',
  'applicationNumber',
  'kind',
  'type',
  'status',
  'applicationLanguageCode',
  'propertyOffice',
  'niceClassification',
  'applicationDate',
  'publicationDate',
  'registrationDate',
  'oppositionEndDate',
  'expirationDate',
  'reproductionURI',
] as const;

export type SynchronizedField = typeof SYNCHRONIZED_FIELDS[number];
export type SynchronizedValues = Partial<Pick<Trademark, SynchronizedField>>;
export type NiceClass = { classNumber: number, details?: string };

// A name without any letter or digit, such as "." or "#", that some offices put in place of the mark's name
export function isPlaceholderName(name?: string | null): boolean {
  return typeof name === 'string' && !/[\p{L}\p{N}]/u.test(name);
}

export function hasWording(niceClass?: NiceClass): boolean {
  return typeof niceClass?.details === 'string' && niceClass.details.trim() !== '';
}

// Whether the office gives a usable value; otherwise the trademark keeps its own
export function officeProvides(field: SynchronizedField, office: TrademarkDTO & { blankImage?: boolean, nameKept?: boolean }): boolean {
  const value = office[field];
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  switch (field) {
    case 'name':
      return !isPlaceholderName(value as string) && !office.nameKept;
    case 'niceClassification':
      return Array.isArray(value) && value.length > 0;
    case 'reproductionURI':
      return !office.blankImage;
    case 'applicationDate':
    case 'publicationDate':
    case 'registrationDate':
    case 'oppositionEndDate':
    case 'expirationDate':
      return moment(value as MomentInput).isValid();
    default:
      return true;
  }
}

// The office classes, each keeping the trademark's wording when the office gives it without
function withOwnWording(own: NiceClass[] | undefined, office: NiceClass[]): NiceClass[] {
  return office.map((niceClass) => {
    if (hasWording(niceClass)) return niceClass;
    const ownClass = (own ?? []).find((candidate) => Number(candidate.classNumber) === Number(niceClass.classNumber));
    return ownClass && hasWording(ownClass) ? { ...niceClass, details: ownClass.details } : niceClass;
  });
}

// Exactly what a confirmed synchronisation writes on the trademark
export function synchronizedValues(current: Trademark, office: TrademarkDTO & { blankImage?: boolean, nameKept?: boolean }): SynchronizedValues {
  const values: Record<string, unknown> = {};
  for (const field of SYNCHRONIZED_FIELDS) {
    if (!officeProvides(field, office)) continue;
    values[field] = field === 'niceClassification'
      ? withOwnWording(current.niceClassification, office.niceClassification as NiceClass[])
      : office[field];
  }
  return values as SynchronizedValues;
}
